import _ from 'lodash';
//
import { IDeal } from 'store/deals/types';
import { EDealComparisonAction, IDealWithEvaluation2 } from 'store/deal-comparison/types';
import apiService from 'services/api';
import apiRoutes from 'services/api/routes';
import { getAccessToken } from 'lib/auth';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import { EDisplay } from 'components/deal-results/product-items';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import { fromApi, toApi } from 'lib/helpers/deal-formatters';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { IStoreState } from 'store';
import { AxiosResponse } from 'axios';
import { mapNameToDisplayName } from 'lib/helpers/field-options';
import { IFieldOptionState } from 'store/field-options/types';

// Add single deal
export const addDeal = (deal: IDeal) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    const dealWithEvaluation: IDealWithEvaluation2 = {
      deal: fromApi(deal),
    };

    dispatch({
      type: EDealComparisonAction.ADD_DEAL,
      dealWithEvaluation,
    });
  };
};

// Add multiple deals to deal comparison from dashboard
export const addDealsFromDashboard = (deals: IDeal[]) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    // Build full object with index and evaluation
    const processedDeals: IDealWithEvaluation2[] = deals.map((deal, index) => ({
      index,
      deal,
    }));

    dispatch({
      type: EDealComparisonAction.ADD_DEALS_FROM_DASHBOARD,
      dealsWithEvaluation: processedDeals,
    });
  };
};

// Remove deal from compare page
export const removeDeal = (index: number) => {
  return {
    type: EDealComparisonAction.REMOVE_DEAL,
    index,
  };
};

export const removeAllDeals = () => {
  return {
    type: EDealComparisonAction.REMOVE_ALL_DEALS,
  };
};

// Shift deal left in comparison table (if possible)
export const shiftDealLeft = (index: number) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    if (index === 0) {
      return;
    }

    const { dealComparison } = getState();
    const { compareDealIds, dealsWithEvaluation } = dealComparison;
    const newDeals: IDealWithEvaluation2[] = _.cloneDeep(dealsWithEvaluation);
    const newCompareDealIds: string[] = _.cloneDeep(compareDealIds);

    const d = newDeals[index];
    const s = newDeals[index - 1];
    const dd = newCompareDealIds[index];
    const ss = newCompareDealIds[index - 1];

    newDeals[index] = s;
    newDeals[index - 1] = d;
    newCompareDealIds[index] = ss;
    newCompareDealIds[index - 1] = dd;

    dispatch({
      type: EDealComparisonAction.UPDATE_DEAL_ORDER,
      dealsWithEvaluation: newDeals,
      compareDealIds: newCompareDealIds,
    });
  };
};

// Shift deal right in comparison table (if possible)
export const shiftDealRight = (index: number) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    const { dealComparison } = getState();
    const { compareDealIds, dealsWithEvaluation } = dealComparison;
    if (index === dealsWithEvaluation.length - 1) {
      return;
    }
    const newDeals: IDealWithEvaluation2[] = _.cloneDeep(dealsWithEvaluation);
    const newCompareDealIds: string[] = _.cloneDeep(compareDealIds);

    const s = newDeals[index];
    const d = newDeals[index + 1];
    const ss = newCompareDealIds[index];
    const dd = newCompareDealIds[index + 1];

    newDeals[index] = d;
    newDeals[index + 1] = s;

    newCompareDealIds[index] = dd;
    newCompareDealIds[index + 1] = ss;

    dispatch({
      type: EDealComparisonAction.UPDATE_DEAL_ORDER,
      dealsWithEvaluation: newDeals,
      compareDealIds: newCompareDealIds,
    });
  };
};

// Deal evaluations
export const evaluateDeals = () => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    // Show loader
    dispatch(setLoading(ELoadingType.SPINNER));

    // Get data from store
    const { dealComparison, dealEvaluation } = getState();
    const { dealsWithEvaluation } = dealComparison;
    const { selectedUnitType, selectedForecastType } = dealEvaluation;

    // Ensure all deals have at least one product
    const wasDealFoundWithNoProduct = dealsWithEvaluation.some((dealWithEvaluation: IDealWithEvaluation2) => {
      const { deal } = dealWithEvaluation;
      if (!deal.products || deal.products.length === 0) {
        const errorToShow = new Error(`Deal "${deal.name}" must have at least one product.`);
        handleError(errorToShow);
        return true;
      }
      return false;
    });

    if (wasDealFoundWithNoProduct) {
      dispatch(setLoading(ELoadingType.NONE));
      return;
    }

    // Create new instance of deals to modify
    const processedDeals = _.cloneDeep(dealsWithEvaluation);

    // Trigger evaluations
    const promises: Array<Promise<AxiosResponse>> = [];
    processedDeals.forEach((d: IDealWithEvaluation2) => {
      promises.push(
        apiService.post(apiRoutes.dealEvaluation, toApi(d.deal), {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
          params: {
            forecast: selectedForecastType,
            outputUnit: selectedUnitType,
          },
        })
      );
    });

    // After successful evaluations -- load into objects
    Promise.allSettled(promises)
      .then((responses: PromiseSettledResult<AxiosResponse>[]) => {
        const errors: string[] = [];

        responses.forEach((res: PromiseSettledResult<AxiosResponse>, index: number) => {
          if (res.status === 'fulfilled') {
            processedDeals[index].evaluation = res.value.data;
          } else {
            const { response } = res.reason;
            if (!response) {
              errors.push(`Error evaluating "${processedDeals[index].deal.name}"`);
            }
            const message = getAxiosErrorMessage(res.reason);
            errors.push(`Error evaluating "${processedDeals[index].deal.name}": ${message}`);
          }
        });

        if (errors.length) {
          handleError(errors.join('\r\n'));
        }

        dispatch({
          type: EDealComparisonAction.ADD_DEALS_FROM_DASHBOARD,
          dealsWithEvaluation: processedDeals,
        });
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

const getSelectedProduct = (products: { name: string; selected: boolean }[] | undefined, selectedProduct: string) => {
  const productSelected = products?.find(
    (product: { name: string; selected: boolean }) => product.name === selectedProduct && product.selected === true
  );
  return productSelected?.selected;
};

const checkDealEvalautionId = (deal: IDeal, dealWithEvaluation: IDealWithEvaluation2) => {
  return deal.id && dealWithEvaluation.deal.id && deal.id === dealWithEvaluation.deal.id;
};

const processSelectedProduct = (dealWithEvaluation: IDealWithEvaluation2, productName: string) => {
  return getSelectedProduct(dealWithEvaluation.deal.productName, productName) ?? false;
};

const processSelectedProductForAllProducts = (dealWithEvaluation: IDealWithEvaluation2) => {
  return getSelectedProduct(dealWithEvaluation.deal.productName, 'All Products') ?? false;
};

const processProductsName = (fieldOptions: IFieldOptionState, pd: { name: string }) => {
  return fieldOptions.products ? mapNameToDisplayName(pd.name, fieldOptions.products) : pd.name;
};

const returnProductName = (
  productNames: { name: string; selected: boolean }[],
  productObj: { name: string; selected: boolean } | undefined
) => {
  return productNames.length > 1 ? [productObj, ...productNames] : productNames;
};

// Update all deals in dealComparison after a saveDeal event
export const updateDealsAfterSave = (deal: IDeal) => {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    const { fieldOptions, dealComparison } = getState();
    const { dealsWithEvaluation } = dealComparison;
    // Filter through the dealsWithEvaluation and update deal objects
    const updatedDealsWithEvaluation = dealsWithEvaluation.map((dealWithEvaluation: IDealWithEvaluation2) => {
      if (checkDealEvalautionId(deal, dealWithEvaluation)) {
        //Below fix will add all latest updated products to deal and later on set selected product that user has set in deal comparison screen if any.
        const productObj = {
          name: 'All Products',
          selected: processSelectedProductForAllProducts(dealWithEvaluation),
        };
        const productNames: { name: string; selected: boolean }[] = [];
        if (deal.products && deal.products.length > 0) {
          deal.products.forEach((pd: { name: string }) => {
            if (pd.name) {
              const productName = processProductsName(fieldOptions, pd);
              productNames.push({
                name: productName,
                selected: processSelectedProduct(dealWithEvaluation, productName),
              });
            }
          });
        }
        return {
          ...dealWithEvaluation,
          deal: {
            ...deal,
            productName: returnProductName(productNames, productObj),
          },
        };
      }
      return dealWithEvaluation;
    });

    // Update deals
    dispatch({
      type: EDealComparisonAction.ADD_DEALS_FROM_DASHBOARD,
      dealsWithEvaluation: updatedDealsWithEvaluation,
    });

    // Trigger re-evaluation
    dispatch(evaluateDeals());
  };
};

export const setSelectedDisplay = (selectedDisplay: EDisplay) => ({
  type: EDealComparisonAction.UPDATE_SELECTED_DISPLAY,
  selectedDisplay,
});

export const updateDeals = (deal: IDeal) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    const { dealComparison } = getState();
    const { dealsWithEvaluation } = dealComparison;

    // Filter through the dealsWithEvaluation and update deal objects
    const updatedDealsWithEvaluation = dealsWithEvaluation.map((dealWithEvaluation: IDealWithEvaluation2) => {
      if (deal.id && dealWithEvaluation.deal.id && deal.id === dealWithEvaluation.deal.id) {
        return {
          ...dealWithEvaluation,
          deal,
        };
      }
      return dealWithEvaluation;
    });
    // Update deals
    dispatch({
      type: EDealComparisonAction.ADD_DEALS_FROM_DASHBOARD,
      dealsWithEvaluation: updatedDealsWithEvaluation,
    });
  };
};

export const setCompareDealIds = (compareDealIds: (string | undefined)[]) => (
  dispatch: ThunkDispatch<void, void, AnyAction>
) => {
  dispatch({
    type: EDealComparisonAction.SET_COMPARE_DEAL_IDS,
    compareDealIds,
  });
};
